import type { ClientMembershipEntry } from "../api/types";

/**
 * `PillVariant` — the presentation state of the card-side "saved" pill, derived
 * purely from the provider's membership entry.
 *
 * - `none`      — not saved (no entry) ⇒ render nothing.
 * - `colleague` — saved & shared by a teammate (`saved_by_me: false`) ⇒
 *   "Condiviso dal team", read-only (D6).
 * - `generic`   — the caller's own save whose authoritative visibility is not
 *   yet known (optimistic save or post-409 refetch in flight/failed) ⇒ base
 *   "Nel Talent Pool" with NO visibility suffix (D5).
 * - `private` / `shared` — the caller's own settled save, suffixed by its
 *   visibility.
 */
export type PillVariant =
  | "none"
  | "private"
  | "shared"
  | "colleague"
  | "generic";

/**
 * Pure selector for the saved pill / heart-fill presentation. DOM-free and
 * side-effect-free so it is unit-testable in Node.
 */
export const pillVariant = (
  entry: ClientMembershipEntry | undefined,
): PillVariant => {
  // Not saved.
  if (entry === undefined) return "none";

  // Saved & shared by a colleague — read-only, wins over any settle-state (D6).
  if (entry.saved_by_me === false) return "colleague";

  // Own save whose server truth is not resolved yet: optimistic entry (no
  // record id) or a post-409 membership refetch in flight/failed (D5). No
  // visibility suffix — the pill stays generic until the refetch settles.
  if (
    entry.refetchPending === true ||
    entry.saved_freelancer_id === undefined
  ) {
    return "generic";
  }

  // Own settled save — suffix by visibility.
  if (entry.visibility === "private") return "private";
  if (entry.visibility === "shared") return "shared";

  // Defensive: `Visibility` is exhaustive above, but never crash the pill.
  return "generic";
};
